import React, { useState } from 'react';
import moment from 'moment';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CalendarWeekView from '../calendar/CalendarWeekView';
import CalendarDayView from '../calendar/CalendarDayView';
import CustomDialog from '../common/CustomDialog';
import NewAvailabilityForm from './NewAvailabilityForm';
import VacationTimeDialog from '../scheduling/VacationTimeDialog';
import useError from '../../hooks/useError';
import { getAwayTimeBlocks, getAvailabilityBlocks } from '../calendar/calendarUtils';
import { useCreateVacationTimeMutation } from '../../redux/services/vacationTimeApi';

const handleSubmit = ({
  createVacationTime,
  vacationTimesQuery,
  setVacationDialogIsOpen,
  setError,
}) => formData => {
  const response = createVacationTime(formData);
  if (response.error) return setError({message: 'Error creating vacation time'});
  setError(null);
  setVacationDialogIsOpen(false);
};

const AvailabilityCalendarScreen = ({
  date,
  awayTimes = [],
  availabilities = [],
  calendarView = 'week',
  boxStyles = {
    backgroundColor: 'white',
    // overflowY: 'hidden',
    borderRadius: 3,
    boxShadow: 1,
    border: 1,
    borderColor: 'divider',
  },
  onDateChange = (e, date) => console.log('Change date', date),
  onCalendarViewChange = (e, view) => console.log('Change calendar view', view),
}) => {
  const [createAvailabilityDialogOpen, setCreateAvailabilityDialogOpen] = useState(false);
  const [vacationTimeDialogOpen, setVacationTimeDialogOpen] = useState(false);
  const [createVacationTime] = useCreateVacationTimeMutation();
  const error = useError();
  const awayTimeBlocks = (
    awayTimes && awayTimes.length
    ? getAwayTimeBlocks({
        timeStart: moment(date).startOf(calendarView).toISOString(),
        timeEnd: moment(date).endOf(calendarView).toISOString(),
        startHour: 8,
        endHour: 20
      })(awayTimes)
    : []
  );
  const availabilityBlocks = (
    availabilities && availabilities.length
    ? getAvailabilityBlocks({ date, startHour: 8, endHour: 20 })(availabilities)
    : []
  );
  const onSubmitVacationTime = handleSubmit({
    createVacationTime,
    setVacationDialogIsOpen: setVacationTimeDialogOpen,
    setError: error.create,
  });
  return (
    <Grid container spacing={1} sx={{ml: {xs: -1}}}>
      <Grid item sx={12} md={9}>
        <Stack direction='row' spacing={1} justifyContent='space-between' pt={1} pb={1}>
          <Stack direction='row' spacing={1}>
            <Button
              color='primary'
              variant='contained'
              size='small'
              onClick={() => setCreateAvailabilityDialogOpen(true)}
            >
              <AddIcon/>
              Add Zone
            </Button>
            <Button
              color='info'
              variant='contained'
              size='small'
              onClick={() => setVacationTimeDialogOpen(true)}
            >
              <AddIcon/>
              Block Away Time
            </Button>
          </Stack>
          <ButtonGroup>
            <Button
              size='small'
              variant={calendarView === 'week' ? 'contained' : 'outlined'}
              color='primary'
              onClick={e => onCalendarViewChange(e, 'week')}
            >
              Week
            </Button>
            <Button
              size='small'
              variant={calendarView === 'day' ? 'contained' : 'outlined'}
              color='primary'
              onClick={e => onCalendarViewChange(e, 'day')}
              style={{minWidth: '60px', fontSize: '0.875rem'}}
            >
              Day
            </Button>
          </ButtonGroup>
        </Stack>
        {calendarView === 'week' &&
          <CalendarWeekView
            date={date}
            awayTimeBlocks={awayTimeBlocks}
            availabilityBlocks={availabilityBlocks}
            boxStyles={boxStyles}
            onDateChange={onDateChange}
          />
        }
        {
          calendarView === 'day' &&
          <CalendarDayView
            date={date}
            awayTimeBlocks={awayTimeBlocks}
            availabilityBlocks={availabilityBlocks}
            boxStyles={boxStyles}
            onDateChange={onDateChange}
          />
        }
      </Grid>
      <Grid item sx={12} md={3}>
        <Box sx={{...boxStyles, p: 0, width: 300}}>
          <Stack spacing={1} p={2}>
            <Typography variant='h2'>Availability Zones</Typography>
            <Typography variant='body1' color='text.muted'>
              {availabilities?.length || 0} zone{availabilities?.length === 1 ? '' : 's'} configured
            </Typography>
          </Stack>
          <Divider />
          <Stack>
            {availabilities?.length > 0 ? (
              availabilities.map((a, i) => (
                <Box key={i} sx={{p: 2}}>
                  <Typography variant='h3'>{a.name}</Typography>
                </Box>
              ))
            ) : (
              <Typography variant='body1' sx={{p: 2}}>
                No availability zones configured. Create zones to specify when and where you are available for appointments.
              </Typography>
            )}
          </Stack>
        </Box>
      </Grid>
      <VacationTimeDialog 
        isOpen={vacationTimeDialogOpen}
        onClose={() => setVacationTimeDialogOpen(false)}
        onSubmit={onSubmitVacationTime}
      />
      <CustomDialog
        open={createAvailabilityDialogOpen}
        onClose={() => setCreateAvailabilityDialogOpen(false)}
        fullWidth={true}
        maxWidth='xs'
      >
        <NewAvailabilityForm />
      </CustomDialog>
    </Grid>
  );
};

export default AvailabilityCalendarScreen;